import { useState, useRef, useEffect, useCallback } from 'react';
import { apiManager } from '@/services/implementations/APIManager';
import { toast } from 'sonner';

export type PaymentProvider = 'alipay' | 'polar';
export type PaymentStatus = 'pending' | 'payed' | 'success' | 'failed' | 'expired';

interface UsePaymentFlowOptions {
  onPaymentSuccess?: () => void;
  onPaymentError?: (error: string) => void;
}

export function usePaymentFlow(options: UsePaymentFlowOptions = {}) {
  // State
  const [isPaymentModalVisible, setIsPaymentModalVisible] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState('');
  const [orderId, setOrderId] = useState('');
  const [polling, setPolling] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>('pending');
  const [selectedPaymentProvider, setSelectedPaymentProvider] = useState<PaymentProvider | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  
  const pollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const stopPolling = useCallback(() => {
    setPolling(false);
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }, []);
  
  const pollPaymentStatus = useCallback(async () => {
    if (!polling || !orderId || !selectedPaymentProvider) return;
    
    try {
      const statusResult = selectedPaymentProvider === 'polar' 
        ? await apiManager.checkPolarPayment(orderId)
        : await apiManager.checkAlipayPayment(orderId);
      
      if (statusResult.status === 'success' || statusResult.status === 'succeeded') {
        setPaymentStatus('success');
        onPaymentSuccess();
      } else if (statusResult.status === 'payed') {
        setPaymentStatus('payed');
        // Continue polling for payed status
        if (polling) {
          pollTimeoutRef.current = setTimeout(() => pollPaymentStatus(), 3000);
        }
      } else if (statusResult.status === 'expire' || statusResult.status === 'not_exist') {
        setPaymentStatus('expired');
        const errorMessage = statusResult.message || 'Payment failed or expired';
        toast.error(errorMessage);
        options.onPaymentError?.(errorMessage);
        resetPayment();
      } else if (statusResult.status === 'error') {
        setPaymentStatus('failed');
        const errorMessage = statusResult.message || 'Payment error';
        toast.error(errorMessage);
        options.onPaymentError?.(errorMessage);
      } else if (polling) {
        // Continue polling
        pollTimeoutRef.current = setTimeout(() => pollPaymentStatus(), 3000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (polling) {
        pollTimeoutRef.current = setTimeout(() => pollPaymentStatus(), 3000);
      }
    }
  }, [polling, orderId, selectedPaymentProvider, options]);
  
  const onPaymentSuccess = useCallback(() => {
    toast.success('🎉 Payment successful 🎉');
    stopPolling();
    setShowConfetti(true);
    setIsPaymentModalVisible(false);
    
    // Auto-hide confetti after 3 seconds
    setTimeout(() => {
      setShowConfetti(false);
      resetPayment();
      options.onPaymentSuccess?.();
    }, 3000);
  }, [stopPolling, options]);
  
  const resetPayment = useCallback(() => {
    setIsPaymentModalVisible(false);
    stopPolling();
    setPaymentUrl('');
    setOrderId('');
    setSelectedPaymentProvider(null);
    setPaymentStatus('pending');
  }, [stopPolling]);
  
  const startPaymentFlow = useCallback((
    provider: PaymentProvider,
    orderData: { orderId: string; paymentUrl: string }
  ) => {
    setSelectedPaymentProvider(provider);
    setOrderId(orderData.orderId);
    setPaymentUrl(orderData.paymentUrl);
    setPaymentStatus('pending');
    
    if (provider === 'polar') {
      // Open Polar payment in new window
      window.open(orderData.paymentUrl, '_blank');
      toast.success('Redirecting to payment page, please complete payment in the new window');
    }
    
    // Show payment modal for both providers
    setIsPaymentModalVisible(true);
    setPolling(true);
  }, []);
  
  // Setup polling effect
  useEffect(() => {
    if (polling && orderId && selectedPaymentProvider) {
      pollPaymentStatus();
    }
  }, [polling, orderId, selectedPaymentProvider, pollPaymentStatus]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);
  
  return {
    // State
    isPaymentModalVisible,
    paymentUrl,
    orderId,
    polling,
    paymentStatus,
    selectedPaymentProvider,
    showConfetti,
    
    // Actions
    startPaymentFlow,
    pollPaymentStatus,
    resetPayment,
    setShowConfetti,
  };
}
